import {useCallback, useEffect, useMemo, useState} from 'react';
import {AI_GATEWAY_URL} from '../../../../components/gen-ai/aiGateway';
import {NovaMcpTool, mapToolCatalogue} from './novaMcpTools';

export type NovaMcpToolsState =
	| {status: 'loading'; tools: NovaMcpTool[]}
	| {status: 'loaded'; tools: NovaMcpTool[]}
	| {status: 'failed'; tools: NovaMcpTool[]};

const NO_TOOLS: NovaMcpTool[] = [];

// One read per page load, shared by every tab that shows the list. A failed read is forgotten, so the
// next read, from a retry or a newly opened tab, tries again.
let pending: Promise<NovaMcpTool[]> | null = null;

function loadMcpTools(): Promise<NovaMcpTool[]> {
	if (!pending) {
		// The gateway authenticates the browser by its Forecast session cookie, as the chat does.
		pending = Promise.resolve()
			.then(() => fetch(`${AI_GATEWAY_URL}/api/mcp-tools`, {credentials: 'include'}))
			.then(response => {
				if (!response.ok) {
					throw new Error(`MCP tool list read failed with ${response.status}`);
				}
				return response.json();
			})
			.then(mapToolCatalogue)
			.catch(error => {
				pending = null;
				throw error;
			});
	}
	return pending;
}

export function resetNovaMcpToolsForTests(): void {
	pending = null;
}

export type NovaMcpToolsLoad = NovaMcpToolsState & {retry: () => void};

// The MCP tools an agent can call, as Forecast-MCP serves them. `retry` reads them again after a
// failed read.
export function useNovaMcpTools(): NovaMcpToolsLoad {
	const [state, setState] = useState<NovaMcpToolsState>({status: 'loading', tools: NO_TOOLS});
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		let isMounted = true;
		loadMcpTools().then(
			tools => isMounted && setState({status: 'loaded', tools}),
			() => isMounted && setState({status: 'failed', tools: NO_TOOLS})
		);
		return () => {
			isMounted = false;
		};
	}, [attempt]);
	const retry = useCallback(() => {
		setState({status: 'loading', tools: NO_TOOLS});
		setAttempt(previous => previous + 1);
	}, []);
	return useMemo(() => ({...state, retry}), [state, retry]);
}
